import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface EmailTemplate {
  id: string;
  template_name: string;
  template_type: string;
  subject: string;
  html_content: string;
  description: string;
  available_placeholders: string[];
  is_system_template: boolean;
}

interface EditEmailTemplateDialogProps {
  template: EmailTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditEmailTemplateDialog({ template, open, onOpenChange, onSuccess }: EditEmailTemplateDialogProps) {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [description, setDescription] = useState("");
  const [copiedPlaceholder, setCopiedPlaceholder] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (template) {
      setSubject(template.subject);
      setHtmlContent(template.html_content);
      setDescription(template.description);
    }
  }, [template]);

  const handleCopyPlaceholder = async (placeholder: string) => {
    try {
      await navigator.clipboard.writeText(placeholder);
      setCopiedPlaceholder(placeholder);
      setTimeout(() => setCopiedPlaceholder(null), 2000);
      toast({
        title: "Copied!",
        description: `${placeholder} copied to clipboard`,
      });
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from("email_templates")
        .update({
          subject,
          html_content: htmlContent,
          description,
          updated_at: new Date().toISOString(),
        })
        .eq("id", template.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Email template updated successfully",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating template:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {template && (
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Email Template</DialogTitle>
            <DialogDescription>
              {template.template_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </DialogDescription>
          </DialogHeader>

          {template.is_system_template && (
          <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-sm text-destructive">
              <strong>Warning:</strong> This is a system authentication template. Changes will affect login/signup emails.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject with {{placeholders}}"
              className="mt-2"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="html_content">HTML Content</Label>
              <span className="text-xs text-muted-foreground">
                {htmlContent.length} characters
              </span>
            </div>
            <Textarea
              id="html_content"
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              rows={18}
              className="font-mono text-xs"
              placeholder="HTML email template with {{placeholders}}"
              required
            />
          </div>

          <div>
            <Label className="mb-2 block">Available Placeholders</Label>
            <div className="flex flex-wrap gap-2">
              {template.available_placeholders.map((placeholder) => (
                <Badge
                  key={placeholder}
                  variant="outline"
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => handleCopyPlaceholder(placeholder)}
                >
                  {placeholder}
                  {copiedPlaceholder === placeholder ? (
                    <Check className="h-3 w-3 ml-1" />
                  ) : (
                    <Copy className="h-3 w-3 ml-1" />
                  )}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Click to copy placeholders to clipboard
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? "Saving..." : "Save Template"}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}
